import { getLocale } from 'JIX/utils.js';

/**
 * Given a date-only string or number in JIX format ("20111010") returns a Date
 * object representing the start of that day in local time.
 *
 * @param {string} str
 */
export function fromJixDate(str) {
    const match = String(str).match(/^(\d{4})(\d{2})(\d{2})$/);
    if (match) {
        // 'T00:00' postfix is needed to force Date to be interpreted as local
        // time and not as UTC.
        return new Date(`${match[1]}-${match[2]}-${match[3]}T00:00`);
    } else {
        throw new Error(`Argument is invalid jixdate: '${str}'`);
    }
}

/**
 * Given a date-only string in ISO8601 format ("2011-10-10") returns a Date
 * object representing the start of that day in local time.
 *
 * @param {string} str
 */
export function fromIsoDate(str) {
    // 'T00:00' postfix is needed to force Date to be interpreted as local
    // time and not as UTC.
    return new Date(`${str}T00:00`);
}

/**
 * Format a date to ISO
 *
 * @param {Date} datetime
 */
export function toIsoDate(datetime) {
    let date = ("0" + datetime.getDate()).slice(-2);
    let month = ("0" + (datetime.getMonth() + 1)).slice(-2);
    return `${datetime.getFullYear()}-${month}-${date}`;
}

/**
 * @param {string} jixDateString Date string in jix format 'yyyymmdd'
 * @returns {string} Date string in date-month-year format 'dd-mm-yyyy'
 */
export function jixToDmy(jixDateString) {
    const year = jixDateString.substring(0, 4);
    const month = jixDateString.substring(4, 6);
    const day = jixDateString.substring(6, 8);
    return day + "-" + month + "-" + year;
}

/**
 * @param {string} dmyString Date string in date-month-year format 'dd-mm-yyyy'
 * @returns {string} Date string in jix format 'yyyymmdd'
 */
export function dmyToJix(dmyString) {
    const day = dmyString.substring(0, 2);
    const month = dmyString.substring(3, 5);
    const year = dmyString.substring(6, 10);
    return year + month + day;
}

/**
 * @param {Date} datetime
 */
export function toHourMinuteString(datetime) {
    return datetime.toTimeString().substr(0, 5);
}

const formatters = {};

/**
 * @param {string} [locale]
 * @returns {(date: Date, options?: Object) => string}
 */
export function getDateFormatter(locale = getLocale()) {
    if (!formatters[locale]) {
        formatters[locale] = (date, options = {}) => {
            const formatOptions = {
                month: options.month || 'long',
                day: 'numeric',
            };

            const addTime = 'addTime' in options ? options.addTime : false;
            if (addTime === true) {
                formatOptions.hour = 'numeric';
                formatOptions.minute = 'numeric';
            } else if (addTime === false) {
                // Default
            } else {
                throw new Error("Invalid addTime value");
            }

            const addYear = 'addYear' in options ? options.addYear : 'auto';
            if (addYear === 'auto') {
                if (date.getYear() !== new Date().getYear()) {
                    formatOptions.year = 'numeric';
                }
            } else if (addYear === true) {
                formatOptions.year = 'numeric';
            } else if (addYear === false) {
                // Default
            } else {
                throw new Error("Invalid addYear value");
            }

            const formatter = new Intl.DateTimeFormat(locale, formatOptions);
            return formatter.format(date);
        };
    }
    return formatters[locale];
}

/**
 * Check if a year is a leap year
 *
 * @param {number} year
 */
export function isLeapYear(year) {
    return (year % 4 === 0) && (year % 100 !== 0) || (year % 400 === 0);
}

const _daysInMonth = [undefined, 31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

/**
 * Get day count of a given month at a given year
 *
 * @param {number} year The year of the month to check
 * @param {number} month The month to check
 */
export function daysInMonth(year, month) {
    const res = _daysInMonth[month];
    return month === 2 && isLeapYear(year) ? res + 1 : res;
}

/**
 * Format a date as a Jix Date
 *
 * @param {Date} dateObject
 */
export function formatAsJixDate(dateObject) {
    let date = ("0" + dateObject.getDate()).slice(-2);
    let month = ("0" + (dateObject.getMonth() + 1)).slice(-2);
    return dateObject.getFullYear() + month + date;
}
